<template>
  <div>
    <h6 class="title is-6">{{ $t("projects.member") }}</h6>
    <div class="buttons">
      <button @click="modalChange('add')" class="button has-tooltip-arrow">
        <span class="icon is-small">
          <i class="fas fa-plus"></i>
        </span>
        <span>{{ $t("projects.addMember") }}</span>
      </button>
    </div>

    <AlertMessage
      v-if="isDangerIfNotAllowDomain"
      color="is-danger"
      :message="$t('projects.domainErrorEmailExist')"
    ></AlertMessage>

    <AlertMessage
      v-if="memberMessage"
      color="is-info"
      :message="memberMessage"
    ></AlertMessage>

    <table class="table is-fullwidth is-hoverable">
      <thead>
        <tr>
          <th>{{ $t("Name") }}</th>
          <th>{{ $t("Email") }}</th>
          <th>{{ $t("projects.userRole") }}</th>
          <th
            @click="goToMfaSetting"
            class="clickable-th"
            style="cursor: pointer;"
            :data-tooltip="$t('profile.mfaTitle')"
          >
            MFA
          </th>
          <th>{{ $t("projects.status") }}</th>
          <th>#</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, userId) in members"
          :key="userId"
          :class="backgroundcolor(filterEmailsByDomain(item.userId))"
        >
          <td>{{ item.userName }}</td>
          <td>{{ item.userId }}</td>
          <td>{{ $t("projects." + item.userRole) }}</td>
          <td :class="{ 'has-text-danger': !item.mfa }">
            {{
              item.mfa
                ? $t("projects.mfaIsEnable")
                : $t("projects.mfaIsDisable")
            }}
          </td>
          <td>{{ item.status ? $t(item.status) : $t("invite") }}</td>
          <td>
            <a @click="modalChange('del', item.userId, item.userRole)">
              <span class="icon-text">
                <span class="icon">
                  <i class="fas fa-edit"></i>
                </span>
              </span>
            </a>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="modal" v-bind:class="{ 'is-active': addModal }">
      <div class="modal-background"></div>
      <div class="modal-card">
        <Form
          @submit="doAddMember"
          :validation-schema="schema"
          :initial-values="{ isAdmin: false, lang: 'ja' }"
          v-slot="{ errors }"
        >
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $t("projects.addMember") }}</p>
          </header>
          <section class="modal-card-body">
            <div class="content">
              <AlertMessage
                color="is-info"
                :message="$t('projects.addMemberMessage')"
              ></AlertMessage>

              <div class="field">
                <label class="label">{{ $t("Email") }} *</label>
                <div class="control">
                  <Field
                    @input="checkDomain"
                    name="userId"
                    class="input"
                    type="email"
                    required="required"
                    :class="{ 'is-danger': errors.userId }"
                  />
                  <span class="help is-danger">{{ errors.userId }}</span>
                </div>
                <div class="control mt-2 ml-1">
                  <label class="label">{{ $t("profile.lang") }} *</label>
                  <div class="select">
                    <Field
                      name="lang"
                      as="select"
                      class="form-control select-lang"
                    >
                      <option value="ja">{{ $t("ja") }}</option>
                      <option value="en-US">{{ $t("en-US") }}</option>
                    </Field>
                  </div>
                </div>
                <div class="control mt-2 ml-1">
                  <Field
                    v-slot="{ field }"
                    name="isAdmin"
                    type="checkbox"
                    :value="true"
                    :unchecked-value="false"
                  >
                    <label>
                      <input
                        type="checkbox"
                        name="isAdmin"
                        v-bind="field"
                        :value="true"
                      />
                      {{ $t("projects.inviteAdmin") }}
                    </label>
                  </Field>
                </div>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button @click="modalChange('add')" class="button" type="reset">
              Cancel
            </button>

            <button
              type="submit"
              class="button ml-3 is-primary"
              v-bind:class="{ 'is-loading': isLoad }"
              :disabled="!isSubmit"
            >
              {{ $t("Add") }}
            </button>
          </footer>
        </Form>
      </div>
    </div>

    <div class="modal" v-bind:class="{ 'is-active': delModal }">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t("projects.changeMember") }}</p>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <h6 class="title is-6">{{ changeEmail }}</h6>
            <AlertMessage
              v-if="changeEmail === userId"
              color="is-danger"
              :message="$t('projects.changeSelf')"
            ></AlertMessage>
            <hr />
            <h6 class="title is-6">{{ $t("projects.changeRole") }}</h6>
            <div v-if="changeUserRole === 'admin'">
              <AlertMessage
                color="is-info"
                :message="$t('projects.changeRoleUserMessage')"
              ></AlertMessage>
              <button
                @click="doChangeRole(changeEmail, 'user')"
                class="button is-primary"
              >
                {{ $t("projects.changeRoleUser") }}
              </button>
            </div>
            <div v-else>
              <AlertMessage
                color="is-info"
                :message="$t('projects.changeRoleAdminMessage')"
              ></AlertMessage>
              <button
                @click="doChangeRole(changeEmail, 'admin')"
                class="button is-primary"
              >
                {{ $t("projects.changeRoleAdmin") }}
              </button>
            </div>
            <hr />
            <h6 class="title is-6">{{ $t("projects.deleteMember") }}</h6>
            <AlertMessage
              color="is-danger"
              :message="$t('projects.deleteMemberMessage')"
            ></AlertMessage>
            <button @click="doDelMember(changeEmail)" class="button is-danger">
              {{ $t("Delete") }}
            </button>
          </div>
        </section>
        <footer class="modal-card-foot text-has-right">
          <button @click="modalChange('del')" class="button">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import AlertMessage from "@/components/AlertMessage.vue";
import imsr from "@/imsr-api";
import { ImsrProjects } from "@/lib/imsrProjects.js";
import { computed, ref } from "@vue/reactivity";
import { Auth } from "aws-amplify";
import { Field, Form } from "vee-validate";
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import * as Yup from "yup";

export default {
  props: {
    projectId: {
      type: String,
      require: true,
    },
    tenantId: {
      type: String,
      require: true,
    },
    company: {
      type: String,
      require: true,
    },
    useDomain: {
      type: String,
      require: true,
    },
    useCount: {
      type: Number,
      require: true,
    },
  },
  components: {
    AlertMessage,
    Form,
    Field,
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const allowedEmailDomains = ref([]);
    const isEnableChackEmailDomain = ref(false);

    const members = ref([]);
    const isLoad = ref(false);
    const addModal = ref(false);
    const memberMessage = ref("");
    const delModal = ref(false);
    const changeEmail = ref("");
    const changeUserRole = ref("");
    const deleteMessage = ref("");
    const isSecondaryUse = ref(false);
    const isSubmit = ref(false);
    const isEnableDomianList = ref(false);
    const isDangerIfNotAllowDomain = ref(false);

    const loadMember = async () => {
      try {
        const imsrProjects = new ImsrProjects();
        members.value = await imsrProjects.getProjectMember(
          props.tenantId,
          props.projectId
        );
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    loadMember();

    const loadDomian = async () => {
      try {
        const imsrProjects = new ImsrProjects();
        const res = await imsrProjects.getProject(
          props.tenantId,
          props.projectId
        );
        isDangerIfNotAllowDomain.value = false;
        allowedEmailDomains.value = res.allowedEmailDomains;
        isEnableChackEmailDomain.value = res.isEnableDomianList;
        if (res.secondaryUse == "true") {
          isSecondaryUse.value = true;
          if (res.isEnableChackEmailDomain) {
            isEnableDomianList.value = true;
            // ドメインリストとオーナドメインを結合
            allowedEmailDomains.value = [
              ...res.allowedEmailDomains,
              props.useDomain,
            ];
          } else {
            isEnableDomianList.value = false;
          }
        } else {
          isSecondaryUse.value = false;
          isEnableDomianList.value = false;
          allowedEmailDomains.value = [props.useDomain];
        }
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    loadDomian();
    watch(
      () => props.key,
      () => {
        loadDomian();
      }
    );

    const modalChange = (modal, email = "", userRole = "") => {
      if (modal == "add") {
        addModal.value = !addModal.value;
      } else {
        if (email != "") {
          changeEmail.value = email;
        }
        if (userRole != "") {
          changeUserRole.value = userRole;
        }
        delModal.value = !delModal.value;
      }
    };

    const schema = Yup.object().shape({
      userId: Yup.string()
        .email()
        .required(t("validate.required"))
        .test(
          "is-authorized-domain",
          "You cannot add email addresses from this domain",
          function(value) {
            return filterEmailsByDomain(value);
          }
        ),
      isAdmin: Yup.boolean()
        .required(t("validate.required"))
        .default(false),
      lang: Yup.string().required(t("validate.required")),
    });

    const doAddMember = async (memberValue) => {
      try {
        isLoad.value = true;
        const params = {
          tenantId: props.tenantId,
          projectId: props.projectId,
          userId: memberValue.userId,
          isAdmin: memberValue.isAdmin,
          lang: memberValue.lang,
        };
        await imsr.imsrPostApi("/projects/member", params);
        loadMember();
        memberMessage.value = t("projects.addMemberSuccess");
      } catch (e) {
        switch (e.message) {
          case "validation error.":
            memberMessage.value = t("projects.addMemberValidateError");
            break;
          case "user exists.":
            memberMessage.value = t("projects.addMemberExist");
            break;
          case "The number of participants has reached the limit.":
            memberMessage.value = t("projects.addMemberLimit");
            break;
          case "Can not use other domain.":
            memberMessage.value = t("projects.domainError");
            break;
          case "User is inviting in another space.":
            memberMessage.value = t("projects.canNotInviteAdmin");
            break;
          default:
            router.push({ name: "InternalServerError" });
        }
      }
      addModal.value = false;
      isLoad.value = false;
    };

    const doDelMember = async (email) => {
      try {
        isLoad.value = true;
        const params = {
          projectId: props.projectId,
          tenantId: props.tenantId,
          userId: email,
        };
        await imsr.imsrDeleteApi("/projects/member", params);
      } catch (e) {
        if (e.message == "can not be delete last admin.") {
          memberMessage.value = t("projects.deleteMemberErrorLastAdmin");
        } else {
          router.push({ name: "InternalServerError" });
        }
      }
      isLoad.value = false;
      delModal.value = false;
      if (email == store.getters.userId) {
        store.commit("unsetAll");
        store.commit("setMessage", t("auth.signOut"));
        await Auth.signOut();
        router.replace({ name: "authSignIn" });
      } else {
        loadMember();
      }
    };

    const doChangeRole = async (email, userRole) => {
      try {
        isLoad.value = true;
        const params = {
          projectId: props.projectId,
          tenantId: props.tenantId,
          userId: email,
          userRole: userRole,
        };
        await imsr.imsrPutApi("/projects/changeRole", params);
      } catch (e) {
        if (e.message == "can not be change last admin.") {
          memberMessage.value = t("projects.changeRoleErrorLastAdmin");
        } else {
          router.push({ name: "InternalServerError" });
        }
      }
      isLoad.value = false;
      delModal.value = false;
      if (email == store.getters.userId) {
        store.commit("unsetAll");
        store.commit("setMessage", t("auth.signOut"));
        await Auth.signOut();
        router.replace({ name: "authSignIn" });
      } else {
        loadMember();
      }
    };

    const checkDomain = (val) => {
      if (filterEmailsByDomain(val.target.value)) {
        isSubmit.value = true;
      } else {
        isSubmit.value = false;
      }
    };

    const filterEmailsByDomain = (email) => {
      // プロジェクトで2次利用が許可されているか？
      if (!isSecondaryUse.value) {
        // 2次利用が許可されていない場合はテナントテーブルに登録されているドメインで判定
        const reg = new RegExp(props.useDomain + "$");
        if (reg.test(email)) {
          return true;
        } else {
          return false;
        }
      } else {
        // 2次利用が許可されている場合はプロジェクトテーブルに登録されているドメインリストで判定
        if (isEnableDomianList.value) {
          for (const domain of allowedEmailDomains.value) {
            const reg = new RegExp(domain + "$");
            if (reg.test(email)) {
              return true;
            }
          }
          return false;
        } else {
          // ドメインリストの使用が設定されていない場合は常ねtrue
          return true;
        }
      }
    };

    const goToMfaSetting = () => {
      router.push({ name: "profileStartMfa" });
    };

    const backgroundcolor = (isDomainNotMatch) => {
      if (!isDomainNotMatch) {
        isDangerIfNotAllowDomain.value = true;
        return "imsr-danger";
      } else {
        return "imsr-notinoperation";
      }
    };

    return {
      isLoad,
      members,
      changeEmail,
      changeUserRole,
      schema,
      addModal,
      delModal,
      modalChange,
      doAddMember,
      doDelMember,
      doChangeRole,
      memberMessage,
      deleteMessage,
      userId: computed(() => store.getters.userId),
      goToMfaSetting,
      checkDomain,
      isSubmit,
      filterEmailsByDomain,
      isDangerIfNotAllowDomain,
      backgroundcolor,
    };
  },
};
</script>

<style>
.imsr-danger {
  background-color: hsl(347, 90%, 96%);
}
.imsr-notinoperation {
  background-color: hsl(183, 90%, 96%);
}
.imsr-camera-icon {
  margin-right: 4px;
}
.clickable-th {
  cursor: pointer;
  color: blue; /* クリックできることを示すための色 */
  text-decoration: underline; /* テキストの下線 */
}

.clickable-th:hover {
  background-color: #f0f0f0; /* ホバー時の背景色 */
}
</style>
